import { useCallback, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import '../backlog/backlog.css';
import { api } from '../../api/client';
import { AppDataGrid, type AppColumn } from '../../components/AppDataGrid';
import { AppToolbar } from '../../components/AppToolbar';
import { FormTitle } from '../../components/FormTitle';
import { useNotify } from '../../components/notifications';
import { useDeleteFlow } from '../../components/confirm';
import { useHotkeys, formatCombo } from '../../components/hotkeys';
import { useFetchMs } from '../../hooks/useFetchMs';
import { DashboardDesignerFrame, type DashboardFrameMode } from './DashboardDesignerFrame';

// ─────────────────────────────────────────────────────────────────────────────
// System ▸ Dashboard ▸ Dashboard — port of the legacy ALPACA LIMS Settings →
// Dashboard list (XAF DashboardData_ListView over dbo.DashboardData), ADR-0016.
//
// List view first (standing rule 10): the dashboard titles.  "Show Dashboard
// Designer" (form-specific, Alt+L) and a row double-click open the selected
// dashboard in the full-page <DashboardDesignerFrame> — the SAME DevExpress v20.1
// designer XAF opens, over the same row, so a dashboard edited here opens
// unchanged in XAF and vice versa.  New opens the designer's own "New dashboard"
// flow (its Save creates the row).  Delete is the audited soft delete; a
// dashboard still assigned in "Assign Dashboard To User" is refused (409).
// ─────────────────────────────────────────────────────────────────────────────

/** Audit form name — matches DashboardsController.AuditFormName (§14b.2). */
export const DASHBOARD_AUDIT_FORM = 'Dashboard';

export interface DashboardRow {
  id: string;
  title: string;
  synchronizeTitle: boolean;
  assignmentCount: number;
}

const COLUMNS: AppColumn<DashboardRow>[] = [
  { field: 'title', header: 'Title', minWidth: 260, text: r => r.title },
  // No "Assigned" column (owner, 2026-09-28): the count of assignment rows read as a
  // role/user count.  The API still returns it and uses it to refuse deleting an
  // assigned dashboard (409).
];

async function fetchDashboards(): Promise<DashboardRow[]> {
  const res = await api.get('/api/v1/dashboards');
  return (res.data as { data: DashboardRow[] }).data ?? [];
}

export default function DashboardsPage() {
  const notify = useNotify();
  const runDelete = useDeleteFlow();
  const { data, isFetching, refetch } = useQuery({ queryKey: ['dashboards'], queryFn: fetchDashboards });
  const rows = useMemo(() => data ?? [], [data]);
  const loadMs = useFetchMs(isFetching);

  const [selected, setSelected] = useState<Set<string | number>>(new Set());
  const [designer, setDesigner] = useState<{ id: string | null; title: string | null; mode: DashboardFrameMode } | null>(null);

  const selectedRow = useCallback((): DashboardRow | null => {
    if (selected.size !== 1) return null;
    const id = String(Array.from(selected)[0]);
    return rows.find(r => r.id === id) ?? null;
  }, [selected, rows]);

  const openDesigner = useCallback((row: DashboardRow | null) => {
    setDesigner({ id: row?.id ?? null, title: row?.title ?? null, mode: 'design' });
  }, []);
  const closeDesigner = useCallback(() => { setDesigner(null); void refetch(); }, [refetch]);

  const designSelected = useCallback(() => {
    const row = selectedRow();
    if (!row) { notify.warning('Select exactly one dashboard to open in the designer.'); return; }
    openDesigner(row);
  }, [selectedRow, notify, openDesigner]);

  const deleteSelected = useCallback(() => {
    if (selected.size === 0) { notify.warning('Select at least one dashboard to delete.'); return; }
    const picked = rows.filter(r => selected.has(r.id));
    const names = picked.map(r => r.title || r.id);
    const shown = names.slice(0, 5).join(', ') + (names.length > 5 ? ` … and ${names.length - 5} more` : '');
    void runDelete({
      formName: DASHBOARD_AUDIT_FORM,
      message: `Delete ${picked.length === 1 ? 'dashboard' : `${picked.length} dashboards`} ${shown}? `
             + 'The legacy ALPACA LIMS stops showing it too.',
      delete: async () => {
        for (const r of picked) await api.delete(`/api/v1/dashboards/${r.id}`);
      },
      onDeleted: () => { setSelected(new Set()); void refetch(); },
    });
  }, [selected, rows, runDelete, notify, refetch]);

  // "Show Dashboard Designer" is a form-specific toolbar button → its key is declared
  // here, next to it (standing rule 14b); New / Delete / Refresh / Export come from
  // <AppToolbar>.  Alt+L matches the Custom Report Builder's "Design".
  useHotkeys(designer ? null : [
    { combo: 'alt+l', label: 'Show Dashboard Designer', group: 'Toolbar', order: 0,
      disabled: selected.size !== 1, run: designSelected },
  ]);

  if (designer) {
    return (
      <div className="bl-page">
        <DashboardDesignerFrame
          dashboardId={designer.id}
          title={designer.title}
          mode={designer.mode}
          onModeChange={mode => setDesigner(d => (d ? { ...d, mode } : d))}
          onBack={closeDesigner}
        />
      </div>
    );
  }

  return (
    <div className="bl-page">
      <div className="bl-grid-area">
        <div className="section-hdr" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
          <FormTitle pageId="dashboards" loadMs={loadMs} loading={isFetching} auditFormName={DASHBOARD_AUDIT_FORM} />
          <AppToolbar
            left={
              <button type="button" className="ca-btn" onClick={designSelected} disabled={selected.size !== 1}
                      title={`Open the selected dashboard in the Dashboard Designer (${formatCombo('alt+l')})`}>
                <i className="ti ti-chart-pie" /> Show Dashboard Designer
              </button>
            }
            onNew={() => openDesigner(null)}
            onDelete={deleteSelected}
            deleteDisabled={selected.size === 0}
            onRefresh={() => void refetch()}
            refreshing={isFetching}
            exportData={{
              filename: 'Dashboards',
              headers: ['Title'],
              rows: rows.map(r => [r.title]),
            }}
          />
        </div>
        <div className="bl-card bl-card-flex">
          <AppDataGrid<DashboardRow>
            rows={rows}
            columns={COLUMNS}
            getRowId={r => r.id}
            loading={isFetching}
            selectable
            selectedIds={selected}
            onSelectionChange={setSelected}
            onRowDoubleClick={row => openDesigner(row)}
            initialSort={{ key: 'title', dir: 'asc' }}
            emptyText="No dashboards found"
          />
        </div>
      </div>
    </div>
  );
}
